import { useEffect, useState } from 'react';

import LoadingWrapper from '../../components/LoadingWrapper';
import WorkdayTopicsTable from '../../components/WorkdayTopics/WorkdayTopicsTable';
import { errorToast } from '../../components/Toasty';

import { getWorkdayTopics } from '../../services/Workday/WorkdayCertificationService';

import EmployeeInfo from '../../types/Employee/EmployeeInfo';
import { WorkdayTopic } from '../../types/Workday/WorkdayTopic';

type WorkdayTopicsProps = {
    userData: EmployeeInfo;
    isMocked: boolean;
};

const WorkdayTopicsPage = ({ userData, isMocked }: WorkdayTopicsProps) => {
    const [topicList, setTopicList] = useState<WorkdayTopic[]>([]);
    const [dataLoading, setDataLoading] = useState(false);

    useEffect(() => {
        if (userData.employeeID === 0) return;

        let cancelled = false;
        setDataLoading(true);
        // No server-side topic filters (#120943 UAT): every topic is loaded and the table's own
        // filters decide what shows. Both flags are off deliberately.
        //   - eligibleCoursesOnly: topics with no eligible courses stay visible so the business can
        //     spot them — the course-count pill marks them as empty rather than hiding the row.
        //   - standardWorkTrainerOnly: this was the gate actually hiding course-less topics. The only
        //     way to link a topic to a Standard Work Trainer certification is the Edit Topics picker,
        //     which will not list a topic with no courses, so a course-less topic could never reach
        //     this page while the flag was on. The page therefore lists all training topics, not only
        //     Standard Work Trainer ones.
        getWorkdayTopics()
            .then(result => {
                if (cancelled) return;
                if (result.ok && result.topics) {
                    // Inactive topics are loaded too (#120943 UAT): the table's Status filter owns
                    // active/inactive visibility, defaulting to Active. The status of any linked
                    // certification is deliberately not considered.
                    setTopicList(result.topics);
                } else {
                    errorToast(result.message ?? 'Error loading topics');
                }
            })
            .finally(() => { if (!cancelled) setDataLoading(false); });

        return () => { cancelled = true; };
    }, [userData]);

    return (
        <div className='standard-work-trainer-page'>
            <div className='row border-bottom mx-3'>
                <div className='col-12 text-center h2 my-3'>
                    Training Topics
                </div>
            </div>
            <div className='row pt-1 mt-1'>
                <LoadingWrapper
                    windSpinner
                    windSpinnerScale={3}
                    showLoading={dataLoading}
                    loadingMessage='Loading'
                >
                    <WorkdayTopicsTable topicList={topicList} />
                </LoadingWrapper>
            </div>
        </div>
    );
};

export default WorkdayTopicsPage;
